import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";

import { NoticeModel } from "features/stock-ordering/core/domain/notice.model";
import {
  getUnreadNotices,
  selectGetUnreadNotices,
  resetGetUnreadNotices,
  GetUnreadNoticesState,
} from "../slices/get-unread-notices.slice";
import {
  postNoticeReadLog,
  selectPostNoticeReadLog,
  resetPostNoticeReadLog,
  PostNoticeReadLogState,
} from "../slices/post-notice-read-log.slice";
import moment from "moment";

interface NoticeReadAcknowledgmentModalProps {
  open: boolean;
  onClose: () => void;
}

export function NoticeReadAcknowledgmentModal(
  props: NoticeReadAcknowledgmentModalProps
) {
  const dispatch = useAppDispatch();
  const getUnreadNoticesState = useAppSelector(selectGetUnreadNotices);
  const postNoticeReadLogState = useAppSelector(selectPostNoticeReadLog);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [noticeQueue, setNoticeQueue] = useState<NoticeModel[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // Fetch all unread notices when the modal opens
  useEffect(() => {
    if (props.open) {
      dispatch(getUnreadNotices());
    }
  }, [dispatch, props.open]);

  // Build the queue from fetched unread notices
  useEffect(() => {
    if (
      getUnreadNoticesState.status === GetUnreadNoticesState.success &&
      getUnreadNoticesState.data
    ) {
      const unread = getUnreadNoticesState.data;
      if (unread.length > 0) {
        setNoticeQueue(unread);
        setCurrentIndex(0);
        setShowModal(true);
        document.body.classList.add("overflow-hidden");
      } else {
        setShowModal(false);
      }
      dispatch(resetGetUnreadNotices());
    }
  }, [getUnreadNoticesState, dispatch]);

  // After successfully logging a read, advance to the next notice
  useEffect(() => {
    if (postNoticeReadLogState.status === PostNoticeReadLogState.success) {
      dispatch(resetPostNoticeReadLog());

      const nextIndex = currentIndex + 1;
      if (nextIndex < noticeQueue.length) {
        // Move to the next unread notice
        setCurrentIndex(nextIndex);
        setHasScrolledToBottom(false);
        setIsChecked(false);
      } else {
        // All notices acknowledged — close modal
        setShowModal(false);
        setNoticeQueue([]);
        setCurrentIndex(0);
        setHasScrolledToBottom(false);
        setIsChecked(false);
        document.body.classList.remove("overflow-hidden");
        props.onClose();
      }
    }
  }, [postNoticeReadLogState, dispatch, currentIndex, noticeQueue.length, props]);

  // Check if content fits without scrolling
  useEffect(() => {
    if (showModal && scrollRef.current) {
      const el = scrollRef.current;
      // Reset scroll position when notice changes
      el.scrollTop = 0;
      if (el.scrollHeight <= el.clientHeight) {
        setHasScrolledToBottom(true);
      }
    }
  }, [showModal, currentIndex]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      const threshold = 20;
      const isAtBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
      if (isAtBottom) {
        setHasScrolledToBottom(true);
      }
    }
  }, []);

  const handleSubmit = () => {
    const currentNotice = noticeQueue[currentIndex];
    if (currentNotice && isChecked) {
      dispatch(postNoticeReadLog({ noticeId: currentNotice.id }));
    }
  };

  const currentNotice = noticeQueue[currentIndex];

  if (!showModal || !currentNotice) {
    return null;
  }

  const remaining = noticeQueue.length - currentIndex;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />

      <div className="relative z-50 bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh] font-[Roboto]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-red-600 to-orange-600 rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold text-white">
              Important Announcement
            </h2>
            {noticeQueue.length > 1 && (
              <span className="text-xs text-red-100">
                {currentIndex + 1} of {noticeQueue.length} unread notices
                {remaining > 1 && ` — ${remaining - 1} more after this`}
              </span>
            )}
          </div>
        </div>

        <div className="px-6 py-3 bg-yellow-50 border-b border-yellow-200">
          <h3 className="font-bold text-yellow-900 text-base">
            {currentNotice.title}
          </h3>
          <span className="text-xs text-yellow-700 italic">
            Published:{" "}
            {currentNotice.published_at
              ? moment(currentNotice.published_at).format(
                  "MMMM D, YYYY [at] h:mm A"
                )
              : "N/A"}
          </span>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-4 min-h-[200px] max-h-[50vh]"
        >
          <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
            {currentNotice.body}
          </div>

          {!hasScrolledToBottom && (
            <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent pt-8 pb-2 text-center">
              <span className="text-xs text-gray-400 italic animate-pulse">
                Please scroll down to read the entire announcement...
              </span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <label
            className={`flex items-start gap-3 cursor-pointer ${
              !hasScrolledToBottom ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={!hasScrolledToBottom}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-700 leading-snug">
              I have read and understood this announcement. I acknowledge that I
              am responsible for complying with the information provided above
              and will adhere to any changes or guidelines outlined in this
              notice.
            </span>
          </label>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleSubmit}
              disabled={!isChecked}
              className={`px-6 py-2 rounded-lg text-sm font-semibold text-white transition-all ${
                isChecked
                  ? "bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              {remaining > 1 ? "Confirm & Next" : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
